import classNames from 'classnames'
import React, {
  ChangeEventHandler,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react'
import { isFirefox } from 'react-device-detect'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Label } from './Label'

export type TextAreaProps = {
  additionalClassName?: string
  defaultValue?: string
  id?: string
  label?: string
  name: string
  onBlur?: ChangeEventHandler<HTMLTextAreaElement>
  onChange?: ChangeEventHandler<HTMLTextAreaElement>
  placeholder?: string
  readOnly?: boolean
  required?: boolean
  /** Result of custom validation provided by a higher order component */
  validation?: true | string
}

export const TextArea = forwardRef<HTMLTextAreaElement, PropsWithChildren<TextAreaProps>>(
  (
    {
      additionalClassName,
      children,
      defaultValue = '',
      id,
      label,
      name,
      onChange: onChangeProps,
      required,
      validation,
      ...inputProps
    },
    ref
  ) => {
    const [value, setValue] = useState<string>(defaultValue)

    const onChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
      e => {
        setValue(e.currentTarget.value)
        onChangeProps?.(e)
      },
      [onChangeProps]
    )

    let containerClassName = classNames(
      'flex w-full items-center font-t text-t-300 text-secondary-darker bg-neutral border rounded transition-hover',
      {
        // Using focus instead of focus-visible for Firefox as the :has pseudo-selector is not supported
        'focus-within:ring': isFirefox,
        '[&:has(:focus-visible)]:ring': !isFirefox,
      }
    )

    if (validation === true && !isNullOrWhiteSpace(value)) {
      containerClassName = classNames(containerClassName, 'border-success', {
        'focus-within:ring-success/40': isFirefox,
        '[&:has(:focus-visible)]:ring-success/40': !isFirefox,
      })
    } else if (validation !== true && !isNullOrWhiteSpace(validation)) {
      containerClassName = classNames(containerClassName, 'border-secondary', {
        'focus-within:ring-error/40': isFirefox,
        '[&:has(:focus-visible)]:ring-error/40': !isFirefox,
      })
    } else {
      containerClassName = classNames(containerClassName, {
        'border-neutral-lighter-alt hover:border-secondary-darker':
          isNullOrWhiteSpace(value),
        'border-secondary-darker': !isNullOrWhiteSpace(value),
        'focus-within:border-secondary-darker focus-within:ring-primary/40': isFirefox,
        '[&:has(:focus-visible)]:border-secondary-darker [&:has(:focus-visible)]:ring-primary/40':
          !isFirefox,
      })
    }

    const inputId = !isNullOrWhiteSpace(id) ? id : name

    return (
      <div className={classNames('w-full', additionalClassName)}>
        {!isNullOrWhiteSpace(label) && (
          <Label additionalClassName="mb-10" htmlFor={inputId} required={required}>
            {label}
          </Label>
        )}

        <div className={containerClassName}>
          <textarea
            {...inputProps}
            className="w-full p-15 placeholder-neutral-dark-alt placeholder-opacity-100 bg-transparent focus:outline-none"
            defaultValue={defaultValue}
            id={inputId}
            name={name}
            onChange={onChange}
            ref={ref}
            required={required}
            rows={6}
          />

          {children}
        </div>

        <div className="min-h-15 mt-5 text-secondary font-t text-t-200 leading-[15px]">
          {validation !== true && !isNullOrWhiteSpace(validation) && validation}
        </div>
      </div>
    )
  }
)
